import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MessageSquare } from 'lucide-react';
import { LessonComment } from '@/types/comments';
import { commentsService } from '@/services/commentsService';
import { auth } from '@/config/firebase';
import { UserAvatar } from '@/components/ui/user-avatar';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { format } from 'date-fns';
import { useAuth } from '@/context/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface LessonCommentsProps {
  lessonId: string;
  onCommentCountChange?: (count: number) => void;
}

export const LessonComments: React.FC<LessonCommentsProps> = ({ lessonId, onCommentCountChange }) => {
  const [comments, setComments] = useState<LessonComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const { displayName } = useAuth();
  
  // Fetch comments
  const fetchComments = async () => {
    try {
      const fetchedComments = await commentsService.getLessonComments(lessonId);
      setComments(fetchedComments);
      onCommentCountChange?.(fetchedComments.length);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };
  
  useEffect(() => {
    fetchComments();
  }, [lessonId]);
  
  // Handle comment submission
  const handleSubmitComment = async () => {
    if (!newComment.trim() || !auth.currentUser) return;
    
    try {
      setIsSubmitting(true);
      
      await commentsService.addComment({
        lessonId,
        userId: auth.currentUser.email || '',
        userDisplayName: displayName || 'Anonymous',
        userProfilePic: auth.currentUser.photoURL || undefined,
        content: newComment.trim()
      });
      
      setNewComment('');
      await fetchComments();
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Comment Input */}
      <div className="flex gap-2">
        <Textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Add a comment..."
          onKeyDown={(e) => {
            // Submit only on Enter without Shift key
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault(); // Prevent default to avoid newline
              handleSubmitComment();
            }
          }}
          disabled={isSubmitting}
          className="min-h-[60px] bg-gray-100 dark:bg-gray-800 resize-vertical"
        />
        <Button
          onClick={handleSubmitComment}
          disabled={!newComment.trim() || isSubmitting}
          size="sm"
        >
          Post
        </Button>
      </div>
      
      {/* Comments List */}
      <ScrollArea className="h-[300px]">
        <div className="space-y-4">
          {comments.map((comment) => (
            <div key={comment.id} className="flex gap-3">
              <UserAvatar size="sm" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{comment.userDisplayName}</span>
                  <span className="text-xs text-gray-500">
                    {format(comment.createdAt.toDate(), 'MMM d, yyyy')}
                  </span>
                  {auth.currentUser?.email === comment.userId && (
                    <div className="flex gap-2 ml-auto">
                      {editingCommentId === comment.id ? (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              try {
                                await commentsService.updateComment(comment.id, editContent);
                                setEditingCommentId(null);
                                fetchComments();
                              } catch (error) {
                                console.error('Error updating comment:', error);
                              }
                            }}
                          >
                            Save
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingCommentId(null)}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingCommentId(comment.id);
                              setEditContent(comment.content);
                            }}
                          >
                            Edit
                          </Button>
                          <AlertDialog>
                            <Button
                              variant="ghost"
                              size="sm"
                              asChild
                            >
                              <AlertDialogTrigger>Delete</AlertDialogTrigger>
                            </Button>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Comment</AlertDialogTitle>
                                <AlertDialogDescription asChild>
                                  <div>Are you sure you want to delete this comment? This action cannot be undone.</div>
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={async () => {
                                    try {
                                      await commentsService.deleteComment(comment.id);
                                      fetchComments();
                                    } catch (error) {
                                      console.error('Error deleting comment:', error);
                                    }
                                  }}
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      )}
                    </div>
                  )}
                </div>
                {editingCommentId === comment.id ? (
                  <Textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && e.preventDefault()}
                    className="mt-1 resize-vertical"
                  />
                ) : (
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 whitespace-pre-wrap">
                    {comment.content}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};